<script setup>
import { ref, watch } from "vue";
import { Field, ErrorMessage } from "vee-validate";
import { useWatchOnce } from "@/composables/useWatchOnce";

const props = defineProps({
  textareaId: {
    type: String,
    default: "exampleInput",
  },
  placeholder: {
    type: String,
    default: "Type here...",
  },
  modelValue: {
    type: String,
    default: "",
  },
  autoHeight: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  readonly: {
    type: Boolean,
    default: false,
  },
  label: {
    type: String,
    default: "",
  },
  rows: {
    type: Number,
    default: 2,
  },
  maxRows: {
    type: Number,
    default: 2,
  },
  size: {
    type: String,
    default: "md",
  },
  hasBoldLabel: {
    type: Boolean,
    default: true,
  },
  name: {
    type: String,
    default: "",
  },
  background: {
    type: String,
    default: "transparent",
  },
});

const textInputRoot = ref(null);
const textareaRef = ref(null);

const isFocused = ref(false);

const emit = defineEmits(["update:modelValue"]);

const inputValue = ref();

const adjustHeight = (target) => {
  if (props.autoHeight) {
    target.style.height = "auto"; // Reset to measure content
    target.style.height = `${target.scrollHeight + 4}px`; // We need + 4 to remove the scrollbar
  }
};

watch(
  () => props.modelValue,
  (val) => {
    inputValue.value = val;
  },
  {
    deep: true,
    immediate: true,
  }
);

useWatchOnce(inputValue, () => {
  if (props.autoHeight) {
    setTimeout(() => {
      adjustHeight(textareaRef.value.element);
    }, 100);
  }
});

watch(inputValue, (newValue) => {
  if (newValue === props.modelValue) return;

  emit("update:modelValue", newValue);
});

onMounted(() => {
  adjustHeight(textareaRef.value.element);
});

const onFocus = () => {
  if (!isFocused.value) {
    isFocused.value = true;
  }
};

const onUnFocus = () => {
  isFocused.value = false;
};
</script>

<template>
  <div ref="textInputRoot" :class="['text-input', { focused: isFocused }]">
    <label
      v-if="label"
      for="input"
      class="label w-100 p-0 text-left text-decoration-none text-neutral-350"
      :class="{ 'fw-bold': hasBoldLabel }"
    >
      {{ label }}
    </label>
    <Field
      :name="name"
      v-model="inputValue"
      aria-describedby="input-true input-false input-help"
      aria-invalid="true"
      v-slot="{ meta, field }"
    >
      <b-form-textarea
        ref="textareaRef"
        :name="name"
        :placeholder="placeholder"
        v-model="inputValue"
        :id="textareaId"
        :size="size"
        :disabled="disabled"
        :readonly="readonly"
        :rows="rows"
        :maxRows="maxRows"
        no-resize
        v-bind="field"
        class="form-control w-100 px-2 custom-textarea heading-text-color"
        :class="{
          'is-valid': meta.valid && meta.touched,
          'is-invalid': !meta.valid && meta.touched,
        }"
        :style="{ background: background }"
        @focus="onFocus"
        @blur="onUnFocus"
        @input="(e) => adjustHeight(e.target)"
      />
    </Field>
    <ErrorMessage
      as="div"
      :name="name"
      v-slot="{ message }"
      class="invalid-feedback"
    >
      {{ message }}
    </ErrorMessage>
  </div>
</template>

<style scoped lang="scss">
.custom-textarea {
  border: 1px solid $neutral-150;
  color: $neutral-600;
  font-size: $font-size-sm;

  &[disabled] {
    background: $neutral-50 !important;
  }

  &:focus-visible {
    outline: none;
    box-shadow: none;
  }

  &::placeholder {
    font-size: $font-size-sm;
  }
}

.invalid-feedback {
  display: block !important;
}

.focused {
  :deep {
    textarea {
      border: 1px solid var(--primary-200) !important;
      font-size: $font-size-sm;
    }
  }
}

.label {
  font-size: $font-size-sm;
  color: $neutral-350;
  margin-bottom: 0.625rem;
}

.invalid-feedback {
  margin-bottom: 4px;
  margin-top: 0.625rem;
  color: $red-150;
  font-size: $font-size-xs;
}
</style>
